import React from "react";
import { DownOutlined } from "@ant-design/icons";
import { Dropdown, Space, Typography, Skeleton } from "antd";
import useAdminDataStore, {
  selectCurrentGroupData,
  selectCurrentGroupId,
  selectAllGroups,
  selectIsDashboardLoading,
} from "../../../../store/adminData/adminData";
import { useNavigate } from "react-router-dom";
import "./GroupDropdown.css";
import DefaultClubLogo from "../../../../images/DefaultClubLogo.svg";

const getGroupLogoUrl = (groupId) => {
  return `https://firebasestorage.googleapis.com/v0/b/${import.meta.env.VITE_FIREBASE_STORAGE_BUCKET}/o/groups%2Flogo%2F${groupId}?alt=media`;
};

const GroupDropdown = ({
  dashboard,
  clubGroups,
  groupNameKeyMap,
  setDashboard,
}) => {
  const navigate = useNavigate();
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const currentGroup = useAdminDataStore(selectCurrentGroupId);
  const allGroups = useAdminDataStore(selectAllGroups);
  const clubGroup = clubGroups[currentGroup];
  const dashboardLoading = useAdminDataStore(selectIsDashboardLoading);

  const items = [
    ...(Object.keys(clubGroups).length > 0
      ? Object.entries(clubGroups).map(([, clubGroupData]) => ({
          key: `${clubGroupData.data.id}|clubadmin`,
          label: (
            <Space>
              <img
                src={getGroupLogoUrl(clubGroupData.data.id)}
                alt="logo"
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  e.target.src = DefaultClubLogo;
                }}
              />
              {`${clubGroupData.data.groupName} Parent`}
            </Space>
          ),
        }))
      : []),
    ...Object.keys(groupNameKeyMap)
      .sort((name1, name2) => name1.localeCompare(name2))
      .map((groupName) => ({
        key: `${groupNameKeyMap[groupName]}|admin`,
        label: (
          <Space>
            <img
              src={getGroupLogoUrl(groupNameKeyMap[groupName])}
              alt="logo"
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.src = DefaultClubLogo;
              }}
            />
            {groupName}
          </Space>
        ),
      })),
  ];

  const restrictedRoutes = [
    "edit",
    "create",
    "/admin/partners/add",
    /^\/admin\/content\/polls\/[a-zA-Z0-9]+$/,
    /^\/admin\/content\/giveaways\/[a-zA-Z0-9]+$/,
  ];

  const handleAdminGroupChange = (value) => {
    if (!value?.key) return;

    const [groupId, selectedDashboard] = value.key.split("|");

    if (selectedDashboard === "admin") {
      const isSwitchAllowed = restrictedRoutes.some((route) => {
        if (typeof route === "string") {
          return window.location.pathname.includes(route);
        } else if (route instanceof RegExp) {
          return route.test(window.location.pathname);
        }
        return false;
      });

      if (isSwitchAllowed) {
        window.alert("You cannot change group at this page");
      } else {
        const group = Object.keys(allGroups).find(
          (group) => group === groupId
        );
        useAdminDataStore.getState().setCurrentGroup(group, "admin");
      }
    } else {
      useAdminDataStore.getState().setCurrentGroup(groupId, selectedDashboard);
    }

    if (selectedDashboard === "admin" || selectedDashboard === "clubadmin") {
      window.localStorage.setItem("currentGroup", groupId);
      window.localStorage.setItem("dashboardType", selectedDashboard);
    }
    if (dashboard !== selectedDashboard) {
      const newPath =
        selectedDashboard === "clubadmin"
          ? "/clubadmin/groups"
          : "/admin/members";
      if (selectedDashboard === "clubadmin") {
        document.documentElement.style.setProperty("--main-color", "#fafafa");
      }
      navigate(newPath);
      setDashboard(selectedDashboard);
    }
  };

  if (dashboardLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Skeleton.Avatar
          active
          size="small"
          shape="circle"
          style={{ width: 24, height: 24 }}
        />
        <Skeleton.Input
          active
          size="small"
          style={{ width: 120, height: 16 }}
        />
      </div>
    );
  }

  return (
    <div style={{ zIndex: 9999, position: "relative" }}>
      {items?.length === 1 ? (
        <Typography>
          <Space
            className="group-dropdown-name--singular"
            style={{ fontFamily: "Open Sans" }}
          >
            <img
              src={getGroupLogoUrl(
                dashboard === "clubadmin" ? clubGroup?.data.id : groupData.id
              )}
              alt="logo"
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.src = DefaultClubLogo;
              }}
            />
            {dashboard === "clubadmin"
              ? clubGroup
                ? `${clubGroup.data.groupName} Parent`
                : ""
              : groupData.groupName}
          </Space>
        </Typography>
      ) : (
        <Dropdown
          className="group-dropdown"
          trigger={["click"]}
          menu={{
            items,
            selectable: true,
            defaultSelectedKeys: [
              dashboard === "clubadmin"
                ? `${clubGroup?.data.id}|clubadmin`
                : `${groupNameKeyMap[groupData.groupName]}|admin`,
            ],
            onClick: handleAdminGroupChange,
            style: {
              maxHeight: "400px",
              overflowY: "auto",
              zIndex: 9999,
            },
          }}
          getPopupContainer={() => document.body}
          overlayStyle={{ zIndex: 9999 }}
        >
          <Typography.Link>
            <Space
              className="group-dropdown-name"
              style={{ fontFamily: "Open Sans" }}
            >
              {dashboard === "clubadmin"
                ? clubGroup
                  ? `${clubGroup.data.groupName} Parent`
                  : ""
                : groupData.groupName}
              <DownOutlined />
            </Space>
          </Typography.Link>
        </Dropdown>
      )}
    </div>
  );
};

export default GroupDropdown;
